import { authedFetch, UnauthorizedError } from '@apis/auth';

export type IInvestigationReportType =
  | 'incidentReport'
  | 'scissorLiftReportExport'
  | 'dailyRoute'
  | 'workersTimesheet'
  | 'observeLocation'
  | 'projectOverview'
  | 'contractorAnalysis';

export interface IInvestigationReportLocation {
  areaId: string;
  areaNick: string;
  floorId: string;
  floorNick: string;
}

/**
 * Project Overview period mode. Mirrors trusstor's `DATE_PICKER_TOGGLE`
 * enum verbatim (uppercase `DATE` | `PERIOD`) so a report archived in
 * D-ORCA round-trips into the Trusstor analysis-center UI unchanged.
 */
export type IPeriodPickerMode = 'DATE' | 'PERIOD';

/**
 * D-orca's view of trusstor's archived `IInvestigationReport`. The BFF
 * proxies the underlying record verbatim; type-specific fields (tag /
 * equipment) are optional at the wire layer because non-dailyRoute
 * records don't carry them.
 *
 * Tz handling: not stamped on the record. The FE formats every timestamp
 * using its working-site tz (selected via `selectSiteTz`), which is the
 * single source of truth for display tz across the app.
 */
export interface IInvestigationReport {
  reportId: string;
  userId: string;
  investigationType: IInvestigationReportType;
  title: string;
  description: string;
  projectId: string;
  /**
   * Epoch ms — the date the analysis is *about* (the subject's day).
   *
   * Optional: the archive holds legacy rows that never stored it (532 of 940
   * on Trusstor staging), and only the three instant-keyed types
   * (`dailyRoute`, `observeLocation`, `incidentReport`) ever carry a
   * meaningful value — the rest are keyed on `dateFrom`/`dateTo`/profession.
   * Read it through `investigatedInstant` rather than `new Date(...)` so the
   * absent case has one documented fallback.
   */
  investigatedTimestamp?: number;
  /** ISO datetime — when the record was persisted in archive. */
  dateGeneratedIso: string;
  // Daily Route variant — populated only when investigationType === 'dailyRoute'.
  entityType?: 'tag' | 'equipment';
  tagId?: string;
  tagNick?: string;
  tagName?: string;
  equipmentId?: string;
  equipmentName?: string;
  equipmentType?: string;
  // Observe Location variant — populated only when investigationType === 'observeLocation'.
  location?: IInvestigationReportLocation[];
  // Contractor Analysis variant — populated only when investigationType === 'contractorAnalysis'.
  professionId?: string;
  professionLabel?: string;
  // Worker Timesheet variant — populated only when investigationType === 'workersTimesheet'.
  // The selected tag-id set and the YYYY-MM-DD date window. Declared
  // FE-side as optional so the report page can read them once the BFF
  // wire schema is extended to forward them (Phase E backend work).
  // Until then, archived workersTimesheet records won't carry these,
  // and the page renders its empty state (per design intent — legacy /
  // mock records without these fields shouldn't crash the page).
  tagIds?: string[];
  dateFrom?: string;
  dateTo?: string;
  // Project Overview variant — populated only when investigationType ===
  // 'projectOverview'. `periodPickerMode` selects daily (`DATE`) vs
  // multi-day (`PERIOD`) rendering; the shared `dateFrom` / `dateTo`
  // (above, YYYY-MM-DD in project tz) carry the window — `dateTo` only
  // for `PERIOD`. The BFF adapter maps these to/from trusstor's archive
  // `fromDate`/`toDate` datetimes so records round-trip into the Trusstor
  // analysis-center UI.
  periodPickerMode?: IPeriodPickerMode;
}

/**
 * POST body. `userId` and `projectId` are filled by the BFF (from auth +
 * site lookup), so the FE never sends them. Same shape regardless of
 * `investigationType` — variant fields are populated only by the
 * matching report type's submit handler.
 *
 * `investigatedTimestamp` is submitted ONLY by the three instant-keyed
 * types, whose subject is a moment or a single day: `dailyRoute` and
 * `observeLocation` compute it via `pickerDateToProjectNoonMs` (working-site
 * tz), `incidentReport` via `pickerDateTimeToProjectMs`. The BFF requires it
 * for exactly those three and rejects nothing for the rest.
 *
 * The other four types must leave it out. They have no instant to report, so
 * the only value they could send is `Date.now()` — which duplicates
 * `dateGenerated` while claiming to be the analysed date, and is
 * indistinguishable from a real one once stored. Their subject lives in their
 * own fields (`dateFrom`/`dateTo`, profession); read those instead.
 */
export interface ICreateInvestigationReportInput {
  investigationType: IInvestigationReportType;
  title: string;
  description: string;
  investigatedTimestamp?: number;
  entityType?: 'tag' | 'equipment';
  tagId?: string;
  tagNick?: string;
  tagName?: string;
  equipmentId?: string;
  equipmentName?: string;
  equipmentType?: string;
  // Observe Location variant — populated only when investigationType === 'observeLocation'.
  location?: IInvestigationReportLocation[];
  // Contractor Analysis variant — populated only when investigationType === 'contractorAnalysis'.
  professionId?: string;
  professionLabel?: string;
  // Worker Timesheet variant — see `IInvestigationReport` for the rationale.
  // Optional on the input so non-workersTimesheet submissions stay unaffected.
  tagIds?: string[];
  dateFrom?: string;
  dateTo?: string;
  // Project Overview variant — see `IInvestigationReport` (shared
  // `dateFrom`/`dateTo` carry the window).
  periodPickerMode?: IPeriodPickerMode;
}

export interface IUpdateInvestigationReportInput {
  title?: string;
  description?: string;
}

/**
 * The instant an archived report is *about*, or undefined when the record
 * doesn't record one.
 *
 * It substitutes NOTHING. This used to fall back to `dateGeneratedIso`, which
 * reads plausibly and is wrong: a report page handed the archive date fetches
 * and renders a complete, believable route for the wrong day, with nothing on
 * screen marking it as substituted — the same "indistinguishable from real
 * data" failure the write-side change exists to eliminate, moved to the read
 * side. `dateGeneratedIso` answers a different question (when it was
 * archived), and the rail already shows it, labelled, in every card's
 * top-right corner.
 *
 * Callers must handle undefined explicitly: report pages render a
 * "no analysed date" state instead of querying, and rail cards omit the date.
 * Prefer a type-specific window (`dateFrom`/`dateTo`) wherever the report has
 * one — only the three instant-keyed types populate this field at all.
 */
export function investigatedInstant(
  record: Pick<IInvestigationReport, 'investigatedTimestamp'>,
): Date | undefined {
  return record.investigatedTimestamp === undefined
    ? undefined
    : new Date(record.investigatedTimestamp);
}

async function handle(res: Response): Promise<never> {
  const body = await res.json().catch(() => null);
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(`request failed: ${res.status} ${body?.error?.message ?? ''}`);
}

export async function createInvestigationReport(
  siteId: string,
  input: ICreateInvestigationReportInput,
  signal?: AbortSignal,
): Promise<{ reportId: string }> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/reports`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
      signal,
    },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as { reportId: string };
}

export async function listInvestigationReports(
  siteId: string,
  signal?: AbortSignal,
): Promise<IInvestigationReport[]> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/reports`,
    { signal },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as IInvestigationReport[];
}

/**
 * Fetches a single archived report. Returns `null` on 404 so callers
 * can distinguish "not found" from network / auth errors.
 */
export async function getInvestigationReport(
  siteId: string,
  reportId: string,
  signal?: AbortSignal,
): Promise<IInvestigationReport | null> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/reports/${encodeURIComponent(reportId)}`,
    { signal },
  );
  if (res.status === 404) return null;
  if (!res.ok) return handle(res);
  return (await res.json()) as IInvestigationReport;
}

export async function updateInvestigationReport(
  siteId: string,
  reportId: string,
  patch: IUpdateInvestigationReportInput,
  signal?: AbortSignal,
): Promise<IInvestigationReport> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/reports/${encodeURIComponent(reportId)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
      signal,
    },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as IInvestigationReport;
}

export function reportRoute(
  siteId: string,
  reportType: IInvestigationReportType,
  reportId: string,
): string {
  return `/analytics/${encodeURIComponent(siteId)}/${reportType}/${reportId}`;
}
